/**
 * Text Helper Utility
 *
 * This utility provides functions to access configured texts from window.texts
 * based on category and key structure.
 */

export interface TextInterface {
  category: string;
  key: string;
  text: string;
}

export class TextHelper {
  private static texts: TextInterface[] = [];

  /**
   * Initialize the text helper with texts from window.texts
   */
  static init(): void {
    if (window.texts && Array.isArray(window.texts)) {
      this.texts = window.texts;
    }
  }

  /**
   * Get text by category and key
   * @param category - The text category (e.g., 'auth.login', 'usage.index')
   * @param key - The text key (e.g., 'head.title', 'head.text')
   * @param defaultText - Default text if not found
   * @returns The configured text or default text
   */
  static getText(category: string, key: string, defaultText: string = ''): string {
    if (!this.texts.length) {
      this.init();
    }

    const textItem = this.texts.find(
      (item) => item.category === category && item.key === key,
    );

    return textItem && textItem.text ? textItem.text : defaultText;
  }

  /**
   * Get all texts for a specific category
   * @param category - The text category
   * @returns Array of text items for the category
   */
  static getTextsForCategory(category: string): TextInterface[] {
    if (!this.texts.length) {
      this.init();
    }

    return this.texts.filter((item) => item.category === category);
  }

  /**
   * Check if a text exists
   * @param category - The text category
   * @param key - The text key
   * @returns Boolean indicating if text exists
   */
  static hasText(category: string, key: string): boolean {
    if (!this.texts.length) {
      this.init();
    }

    return this.texts.some(
      (item) => item.category === category && item.key === key,
    );
  }

  /**
   * Process text with link replacements
   * @param text - The text to process
   * @returns Processed text with HTML links
   */
  static processTextWithLinks(text: string): string {
    return text.replace(
      /\[link url="([^"]+)"\](.*?)\[\/link\]/g,
      '<a href="$1">$2</a>',
    );
  }
}

/**
 * Composable for accessing texts
 */
export function useTexts() {
  const getText = (
    category: string,
    key: string,
    defaultText: string = '',
  ): string => TextHelper.getText(category, key, defaultText);

  const getTextsForCategory = (
    category: string,
  ): TextInterface[] => TextHelper.getTextsForCategory(category);

  const hasText = (
    category: string,
    key: string,
  ): boolean => TextHelper.hasText(category, key);

  const processTextWithLinks = (
    text: string,
  ): string => TextHelper.processTextWithLinks(text);

  return {
    getText,
    getTextsForCategory,
    hasText,
    processTextWithLinks,
  };
}

/**
 * Global function for accessing texts (for use in templates)
 */
export const $text = (
  category: string,
  key: string,
  defaultText: string = '',
): string => TextHelper.getText(category, key, defaultText);

// Initialize on load
if (typeof window !== 'undefined') {
  TextHelper.init();
}
